// Restaurant members — GET/POST/PUT/DELETE /restaurants/{rid}/users.
// Members are added by EMAIL: the server resolves the account or, for a
// never-seen address, creates one and hands back the INVITE LINK that sets
// its password — the server emails it to them and this page shows it to
// copy as well (in case the email goes astray). Adding an existing member again moves their role (the POST is an
// upsert). A reset link for a member who forgot comes from the same page.

import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { api } from '../../lib/api';
import { ROLES, type Membership, type PortalRole } from './types';

export function Members() {
  const { rid = '' } = useParams();
  const [members, setMembers] = useState<Membership[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<PortalRole>('user');
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState<{ email: string; url: string; linkType: 'invite' | 'reset'; expiresAt: string } | null>(null);

  const load = useCallback(async () => {
    try {
      setMembers(await api.get<Membership[]>(`/restaurants/${rid}/users`));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load.');
    }
  }, [rid]);

  useEffect(() => { void load(); }, [load]);

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    try {
      await fn();
      setError(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  const add = () => act(async () => {
    const added = await api.post<Membership & { inviteLink?: { url: string; linkType: 'invite'; expiresAt: string } }>(`/restaurants/${rid}/users`, { email: email.trim(), role });
    setLink(added.inviteLink ? { email: added.email, ...added.inviteLink } : null);
    setEmail('');
  });

  const resetLink = (m: Membership) => act(async () => {
    const made = await api.post<{ url: string; linkType: 'reset'; expiresAt: string }>(`/restaurants/${rid}/users/${m.userId}/password-links`);
    setLink({ email: m.email, ...made });
  });

  const remove = (m: Membership) => {
    if (!confirm(`Remove ${m.email} from this restaurant?`)) return;
    void act(() => api.del(`/restaurants/${rid}/users/${m.userId}`));
  };

  return (
    <>
      <div className="page-head">
        <div>
          <div className="overline">Restaurant</div>
          <h1>Members</h1>
        </div>
      </div>
      {error && <div className="err" role="alert">{error}</div>}
      {link && (
        <div className="panel" style={{ maxWidth: 720, marginBottom: 20, borderColor: 'var(--primary)' }} data-section="password-link" data-link-type={link.linkType}>
          <h2>{link.linkType === 'invite' ? 'Invite link' : 'Reset link'} for {link.email}</h2>
          <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: '8px 0 12px' }}>
            We've emailed this to them. You can also send it yourself — it sets their password and
            signs them in, once, until {new Date(link.expiresAt).toLocaleString('en-AU')}.
          </p>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input className="input" readOnly aria-label="Password link" value={link.url} onFocus={(e) => e.currentTarget.select()} />
            <button className="btn btn-outline btn-sm" onClick={() => void navigator.clipboard?.writeText(link.url)}>Copy</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setLink(null)}>Done</button>
          </div>
        </div>
      )}

      <div className="panel" style={{ maxWidth: 720, marginBottom: 20 }} data-section="add-member">
        <h2>Add a member</h2>
        <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: '8px 0 12px' }}>
          Add anyone by email. If they have never signed in, their account is created
          and you get an invite link to send them. Admins manage the restaurant;
          users can work in its stores.
        </p>
        <form style={{ display: 'flex', gap: 10, alignItems: 'center' }}
          onSubmit={(e) => { e.preventDefault(); if (email.trim()) void add(); }}>
          <input className="input" type="email" aria-label="Member email" placeholder="name@example.com.au" value={email}
            style={{ maxWidth: 320 }} onChange={(e) => setEmail(e.target.value)} />
          <select className="input" aria-label="New member role" style={{ width: 'auto' }} value={role}
            onChange={(e) => setRole(e.target.value as PortalRole)}>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !email.trim()}>
            Add member
          </button>
        </form>
      </div>

      {members === null && !error && <div className="loading">Loading…</div>}
      {members?.length === 0 && <div className="loading">No members yet.</div>}
      {members && members.length > 0 && (
        <table className="tbl" style={{ maxWidth: 720 }} data-section="members">
          <thead>
            <tr>
              <th>Member</th>
              <th>Role</th>
              <th>Added</th>
              <th />
            </tr>
          </thead>
          <tbody style={{ cursor: 'default' }}>
            {members.map((m) => (
              <tr key={m.userId} data-member={m.email} data-role={m.role} style={{ cursor: 'default' }}>
                <td>
                  <span style={{ fontWeight: 700 }}>{m.email}</span>
                  {m.displayName && <span style={{ color: 'var(--ink-3)', marginLeft: 8 }}>{m.displayName}</span>}
                </td>
                <td>
                  <select className="input" aria-label={`Role for ${m.email}`} style={{ width: 'auto', padding: '4px 8px' }}
                    value={m.role} disabled={busy}
                    onChange={(e) => void act(() => api.put(`/restaurants/${rid}/users/${m.userId}`, { role: e.target.value }))}>
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td style={{ color: 'var(--ink-3)' }}>
                  {new Date(m.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                </td>
                <td className="num">
                  <button className="btn btn-ghost btn-sm" disabled={busy} aria-label={`Reset link for ${m.email}`}
                    onClick={() => void resetLink(m)}>
                    Reset link
                  </button>{' '}
                  <button className="btn btn-danger btn-sm" disabled={busy} aria-label={`Remove ${m.email}`}
                    onClick={() => remove(m)}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
